import type { FieldValue } from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import type { FilterValues, SetValueProps } from '@src/components/FilterDropDown/FilterDropDown.interfaces';

import type { WidgetFilters } from '../contexts/EventsByScheduleContext';

/**
 * Injected handlers so table UI (column search / Ant Design table filters) can update the same
 * widget filter store as tags and the side panel, without importing dashboard context in transformers.
 */
export type WidgetTableFilterHandlers = {
  /** Persists a single field change (e.g. column search confirm) into widget filter state. */
  applyWidgetFilterField: (columnKey: string, value: FieldValue) => void;
  /** Applies all filter keys from an Ant Design `onTableChange` filters object in one update. */
  applyWidgetTableFiltersBatch: (
    _pagination: unknown,
    tableFilters: Record<string, unknown | unknown[]>,
  ) => void;
};

/**
 * Returns the next `WidgetFilters` snapshot after setting one filter field’s `value`.
 * Preserves other fields and merges into the existing entry for `columnKey` when present.
 */
export const mergeWidgetFilterField = (
  prev: WidgetFilters | undefined,
  widgetId: string,
  widgetFallback: unknown,
  columnKey: string,
  value: FieldValue,
): WidgetFilters => ({
  widgetId,
  widget: prev?.widget ?? widgetFallback,
  filters: {
    ...(prev?.filters ?? {}),
    [columnKey]: {
      ...(prev?.filters?.[columnKey] ?? {}),
      value,
    },
  },
});

/**
 * Returns the next `WidgetFilters` snapshot after applying a full `SetValueProps` payload
 * (used by tag clear / FilterActiveTags and similar flows that send label, valueTranslated, etc.).
 */
export const mergeWidgetFilterSetValueProps = (
  prev: WidgetFilters | undefined,
  widgetId: string,
  widgetFallback: unknown,
  props: SetValueProps,
): WidgetFilters => {
  const { key, value, label, valueTranslated, valueType, clearValue } = props;
  return {
    widgetId,
    widget: prev?.widget ?? widgetFallback,
    filters: {
      ...(prev?.filters ?? {}),
      [key]: {
        ...(prev?.filters?.[key] ?? {}),
        value,
        ...(label !== undefined ? { label } : {}),
        ...(valueTranslated !== undefined ? { valueTranslated } : {}),
        ...(valueType !== undefined ? { valueType } : {}),
        ...(clearValue !== undefined ? { clearValue } : {}),
      },
    } as FilterValues,
  };
};

/**
 * Returns the next `WidgetFilters` snapshot after syncing with Ant Design table `filters`.
 * Empty or cleared values remove that key; if no keys remain, `filters` becomes `null`.
 */
export const mergeWidgetTableFiltersBatch = (
  prev: WidgetFilters | undefined,
  widgetId: string,
  widgetFallback: unknown,
  tableFilters: Record<string, unknown | unknown[]>,
): WidgetFilters => {
  const nextFilters: FilterValues = { ...(prev?.filters ?? {}) };
  Object.entries(tableFilters || {}).forEach(([key, raw]) => {
    const filterValue = (Array.isArray(raw) ? raw[0] : raw) as FieldValue;
    if (filterValue === undefined || filterValue === null || filterValue === '') {
      delete nextFilters[key];
    } else {
      nextFilters[key] = {
        ...(nextFilters[key] ?? {}),
        value: filterValue,
      };
    }
  });
  return {
    widgetId,
    widget: prev?.widget ?? widgetFallback,
    filters: Object.keys(nextFilters).length ? nextFilters : null,
  };
};
